import * as Sentry from "@sentry/nextjs";
import { difference } from "es-toolkit";
import { makeObservable, observable, toJS } from "mobx";

import type { OperatorNodeModel } from "./OperatorNodeModel";
import type { InlineEditableNode } from "./visitors/NodeVisitor";
import type { DocumentModel } from "@/models/DocumentModel";
import type {
  NodeId,
  SerializedNode,
  NodeProps,
  NodeModel,
  AllRequredKeys,
} from "@/models/nodes/types";
import type { NoNodeProps, OperatorTypeValue, NodeJson } from "@corca/data";

type CleanupCallback = () => void;

export type ClassOf<T> = {
  new (...args: any[]): T; // eslint-disable-line @typescript-eslint/no-explicit-any
  type: string;
};

const ATTACHMENT_TYPES: NodeModel["type"][] = ["label", "decorator"];

/**
 * Lets `BaseNodeModel` pass itself where a concrete `NodeModel` is expected.
 *
 * Use it only for handing out `this` from within `BaseNodeModel`.
 */
type NodeModelLike = never;

export abstract class BaseNodeModel<
  Props extends NodeProps = NoNodeProps,
  PartialProps extends object = Props extends NoNodeProps ? Props : Partial<Props>,
> {
  id: NodeId;

  abstract type: string;

  readonly inlineEditable: boolean = false;

  /**
   * If true, treats whole node as atomic unit and prevents cursor from moving inside it.
   */
  readonly isAtom: boolean = false;

  get isNested() {
    return false;
  }

  protected _props: Props;

  get props(): Readonly<Props> {
    return this._props;
  }

  updateProps(updates: Partial<Props>): void {
    const hasChanges = Object.keys(updates).some(
      (key: keyof Props) => !(key in this._props) || this._props[key] !== updates[key],
    );
    if (hasChanges) {
      this.setProps({ ...this._props, ...updates });
    }
  }

  setProps(props: Props): void {
    this._props = props;
  }

  childrenIds: NodeId[] = [];

  doc: DocumentModel;

  cleanupCallbacks: Array<CleanupCallback> = [];

  parent: NodeModel | undefined = undefined;

  constructor(
    doc: DocumentModel,
    id?: string,
    props: PartialProps = {} as PartialProps,
    children: NodeModel[] | NodeId[] = [],
  ) {
    this.doc = doc;
    this.id = id ?? doc.generateNodeId();
    this.appendChildren(children, true);
    this._props = this.fillRequiredProps(props);
    makeObservable<typeof this, "children" | "_props">(this, {
      id: false,
      parent: true,
      childrenIds: true,
      nodes: true,
      children: true,
      _props: observable.ref,
      prevSiblings: true,
      nextSiblings: true,
      ancestors: true,
      appendChildren: true,
      appendChild: true,
      prependChildren: true,
      prependChild: true,
      insertSiblingAfter: true,
      insertSiblingBefore: true,
      setChildren: true,
      removeFromParent: true,
      replaceWith: true,
      serialized: false,
      descendants: true,
      index: true,
      setProps: true,
      updateProps: true,
    });
  }

  get isEditable(): boolean {
    return false;
  }

  isInlineEditable(): this is InlineEditableNode {
    return this.inlineEditable;
  }

  protected abstract fillRequiredProps(props: PartialProps): Props;

  onCleanup(callback: CleanupCallback): void {
    this.cleanupCallbacks.push(callback);
  }

  cleanup() {
    this.cleanupCallbacks.forEach((c) => c());
    this.cleanupCallbacks = [];
  }

  is<C extends NodeModel>(type: ClassOf<C>): this is C;

  is<T extends OperatorTypeValue>(type: T): this is OperatorNodeModel<T>;

  is<C extends NodeModel>(type: OperatorTypeValue | ClassOf<C>): boolean {
    if (typeof type === "string") {
      // implemented in OperatorNodeModel
      return false;
    }
    return this.type === type.type;
  }

  get nodes() {
    return this.children.filter((n) => !ATTACHMENT_TYPES.includes(n.type));
  }

  get attachments() {
    return this.children.filter((n) => ATTACHMENT_TYPES.includes(n.type));
  }

  protected get children(): NodeModel[] {
    return (this.childrenIds.map((nodeId) => this.doc.nodes.get(nodeId)) as NodeModel[]).filter(
      Boolean,
    );
  }

  get descendants(): NodeModel[] {
    return this.children.reduce<NodeModel[]>((r, n) => [...r, n, ...n.descendants], []);
  }

  hasAncestorType(type: string): boolean {
    return !!this.ancestors.find((ancestor) => ancestor.type === type);
  }

  /** Index of the node in parent.children */
  get index() {
    return (this.parent?.childrenIds || []).indexOf(this.id);
  }

  appendChildren(children: NodeModel[] | NodeId[], skipTransform?: boolean) {
    children.forEach((child) => this.appendChild(child, skipTransform));
  }

  prependChildren(children: NodeModel[] | NodeId[]) {
    children
      .slice()
      .reverse()
      .forEach((child) => this.prependChild(child));
  }

  prepareUpdate(_skipTransform?: boolean) {
    this.doc.nodesToUpdate.add(this.id);
  }

  setChildren(children: NodeModel[] | NodeId[], skipTransform?: boolean) {
    const ids = children.map((n) => (typeof n === "string" ? n : n.id));
    const added = difference(ids, this.childrenIds);
    const removed = difference(this.childrenIds, ids);

    for (const id of added) {
      this.appendChild(id, skipTransform);
    }

    for (const id of removed) {
      const node = this.doc.nodes.get(id);
      if (node && node.parent?.id === this.id) {
        this.doc.removeNode(node);
      }
    }

    this.childrenIds = ids;
  }

  /**
   * @deprecated
   * Use doc.removeNode() instead of this method to remove a node from the document.
   *
   * TODO: move this method to node manager.
   * @see https://linear.app/corca-research/issue/D-3109/node-manager
   */
  removeFromParent() {
    if (this.parent && this.index !== -1) {
      this.parent.childrenIds.splice(this.index, 1);
      this.parent.prepareUpdate();
    }
  }

  private checkCircular(node: NodeModel): boolean {
    if ((this as NodeModelLike) === node) return true;
    if (this.ancestors.find((n) => n === node)) return true;

    return node.childrenPlease.some((n) => this.checkCircular(n));
  }

  private checkCircularAndReport(node: NodeModel): boolean {
    if (this.doc.isInitialLoad) return false;

    if (this.checkCircular(node)) {
      console.error("Attempt to add node", node.id, "to itself or its descendant:", this.id);
      return true;
    }
    return false;
  }

  /**
   * Replaces the current node with another node.
   */
  replaceWith(newNode: NodeModel) {
    if (this.parent && this.index !== -1) {
      if (this.parent.checkCircularAndReport(newNode)) {
        return;
      }
      newNode.parent = this.parent;
      this.parent.childrenIds[this.index] = newNode.id;
      this.parent.prepareUpdate();
      this.doc.registerNode(newNode);
      this.doc.unregisterNode(this as NodeModelLike);
    }
  }

  /**
   * Appends a child node.
   */
  appendChild(nodeOrId: NodeModel | NodeId, skipTransform?: boolean) {
    const node = typeof nodeOrId === "string" ? this.doc.nodes.get(nodeOrId) : nodeOrId;

    if (node) {
      if (this.checkCircularAndReport(node)) {
        return;
      }
      node.removeFromParent();
      this.doc.registerNode(node);
      this.childrenIds.push(node.id);
      node.parent = this as NodeModelLike;
      node.prepareUpdate(skipTransform);
    } else if (typeof nodeOrId === "string") {
      this.childrenIds.push(nodeOrId);
      this.doc.nodesToUpdate.add(nodeOrId);
    }
    this.prepareUpdate(skipTransform);
  }

  prependChild(nodeOrId: NodeModel | NodeId) {
    const node = typeof nodeOrId === "string" ? this.doc.nodes.get(nodeOrId) : nodeOrId;

    if (node) {
      if (this.checkCircularAndReport(node)) {
        return;
      }
      node.removeFromParent();
      this.doc.registerNode(node);
      this.childrenIds.unshift(node.id);
      node.parent = this as NodeModelLike;
    } else if (typeof nodeOrId === "string") {
      this.childrenIds.unshift(nodeOrId);
    }
    this.prepareUpdate();
  }

  /**
   * Inserts a node before the current node.
   */
  insertSiblingBefore(node: NodeModel) {
    if (this.parent) {
      if (this.parent.checkCircularAndReport(node)) {
        return;
      }
      node.removeFromParent();
      this.doc.registerNode(node);
      this.parent.childrenIds.splice(this.index, 0, node.id);
      node.parent = this.parent;
      this.parent.prepareUpdate();
    }
  }

  /**
   * Inserts a node after the current node.
   */
  insertSiblingAfter(node: NodeModel) {
    if (this.parent) {
      if (this.parent.checkCircularAndReport(node)) {
        return;
      }
      node.removeFromParent();
      this.doc.registerNode(node);
      this.parent.childrenIds.splice(this.index + 1, 0, node.id);
      node.parent = this.parent;
      this.parent.prepareUpdate();
    }
  }

  findNextSibling(node: NodeModel | BaseNodeModel | undefined): NodeModel | null {
    const nextParentSibling = node?.nextSiblings?.[0];
    if (nextParentSibling) {
      return nextParentSibling;
    }
    if (node && node.parent) {
      return this.findNextSibling(node.parent);
    }
    return null;
  }

  findPrevParentChild(node: NodeModel | undefined): NodeModel | null {
    const prevParentLastChildNode = node?.nodes?.slice(-1)?.[0];
    if (prevParentLastChildNode) {
      return this.findPrevParentChild(prevParentLastChildNode);
    }
    if (node) {
      return node;
    }
    return null;
  }

  get siblings() {
    return this.parent?.nodes || [];
  }

  get prevSiblings() {
    const index = this.siblings.indexOf(this as NodeModelLike);
    if (index === -1) return [];
    return this.siblings.slice(0, index).reverse();
  }

  get nextSiblings() {
    const index = this.siblings.indexOf(this as NodeModelLike);
    if (index === -1) return [];
    return this.siblings.slice(index + 1);
  }

  get ancestors(): NodeModel[] {
    const ancestorsList: NodeModel[] = [];
    let currentAncestor: NodeModel | undefined = this.parent;

    while (currentAncestor) {
      ancestorsList.push(currentAncestor);
      currentAncestor = currentAncestor.parent;
    }

    return ancestorsList;
  }

  toJSON(): NodeJson {
    const { id, type, props, children } = this;
    return {
      id,
      type,
      props: toJS(props),
      children: children.map((n) => n.toJSON()),
    } as NodeJson;
  }

  get serialized(): SerializedNode {
    const { id, type, childrenIds, props } = this;
    return {
      id,
      type,
      props: toJS(props),
      childrenIds: toJS(childrenIds),
    };
  }

  /**
   * Don't use children directly, please add getters on extend classes that
   * use chidlren internally.
   * @deprecated
   */
  get childrenPlease() {
    return this.children;
  }

  protected reportMissingProps(props: PartialProps, required: AllRequredKeys<Props>) {
    const missingProps = required.filter((field) => !(field in props));
    if (missingProps.length > 0) {
      const message = `Missing required props for ${this.constructor.name} (${missingProps.join(",")})`;
      console.error(message, props);
      Sentry.captureMessage(message, { extra: { props } });
    }
  }
}
